import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { calculateAge, convertToIsoFormat, validateDateFormat } from '../utils/ageCalculator';
import { doc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const ProfileSetup: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    nickname: '',
    instrument: '',
    birthdate: '',
    role: 'student' as 'student' | 'teacher' | 'admin',
    practiceSessionsPerWeek: 5,
    practiceSessionDuration: 30
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { currentUser, refreshUserData } = useAuth();
  const navigate = useNavigate();

  // Prevent window close if profile is incomplete
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'You must complete your profile setup before closing the window.';
      return 'You must complete your profile setup before closing the window.';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Initialize form data with current user data if available
  useEffect(() => {
    if (currentUser) {
      // If user is admin, redirect to admin dashboard
      if (currentUser.role === 'admin') {
        navigate('/addminn/dashboard');
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        name: currentUser.name || '',
        nickname: currentUser.nickname || '',
        instrument: currentUser.instrument || '',
        birthdate: currentUser.birthdate ? currentUser.birthdate.split('-').reverse().join('-') : '',
        role: currentUser.role || 'student',
        practiceSessionsPerWeek: currentUser.practiceSessionsPerWeek || 5,
        practiceSessionDuration: currentUser.practiceSessionDuration || 30
      }));
    }
  }, [currentUser, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'practiceSessionsPerWeek' || name === 'practiceSessionDuration' 
        ? parseInt(value) || 0 
        : value
    }));
  };

  const handleNumberChange = (name: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const checkNicknameUniqueness = async (nickname: string): Promise<boolean> => {
    try {
      const nicknameQuery = query(
        collection(db, 'users'),
        where('nickname', '==', nickname.trim())
      );
      const querySnapshot = await getDocs(nicknameQuery);
      return querySnapshot.empty; // Returns true if nickname is unique (no documents found)
    } catch (error) {
      console.error('Error checking nickname uniqueness:', error);
      return false; // If there's an error, assume nickname is not unique to be safe
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      setError('No user found. Please log in again.');
      return;
    }

    // Check nickname uniqueness
    const isNicknameUnique = await checkNicknameUniqueness(formData.nickname);
    if (!isNicknameUnique) {
      return setError('This nickname is already taken. Please choose a different one.');
    }

    // Validate birthdate for students
    if (formData.role === 'student' && (!formData.birthdate || !validateDateFormat(formData.birthdate))) {
      return setError('Please enter a valid birthdate in DD-MM-YYYY format');
    }
    
    try {
      setError('');
      setLoading(true);
      
      const userData = {
        name: formData.name,
        nickname: formData.nickname,
        instrument: formData.instrument,
        birthdate: formData.role === 'student' ? convertToIsoFormat(formData.birthdate) : '',
        // For students, set initial goals as pending approval
        ...(formData.role === 'student' ? {
          practiceSessionsPerWeek: 30, // Default values until approved
          practiceSessionDuration: 5,
          pendingGoalChanges: {
            practiceSessionDuration: formData.practiceSessionDuration,
            practiceSessionsPerWeek: formData.practiceSessionsPerWeek,
            requestedAt: new Date(),
            status: 'pending' as const
          }
        } : {
          // For teachers, don't save practice goals as they don't practice
          practiceSessionsPerWeek: 0,
          practiceSessionDuration: 0,
        }),
        hasCompletedSetup: true
      };
      
      // Update user document in Firestore
      await updateDoc(doc(db, 'users', currentUser.uid), userData);
      
      // Refresh user data to get updated information
      await refreshUserData();
      
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      setError('Failed to save profile information');
      console.error(error);
    }
    
    setLoading(false);
  };

  const isFormValid = () => {
    if (!formData.name.trim() || !formData.nickname.trim() || !formData.instrument.trim()) {
      return false;
    }
    
    if (formData.role === 'student' && (!formData.birthdate || !validateDateFormat(formData.birthdate))) {
      return false;
    }

    // Only validate practice goals for students
    if (formData.role === 'student') {
      if (formData.practiceSessionsPerWeek < 1 || formData.practiceSessionsPerWeek > 7) {
        return false;
      }

      if (formData.practiceSessionDuration < 10 || formData.practiceSessionDuration > 120) {
        return false;
      }
    }
    
    return true;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Complete Your Profile
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Please provide some additional information to get started
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Account Type
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="role"
                    value="student"
                    checked={formData.role === 'student'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  Student
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="role"
                    value="teacher"
                    checked={formData.role === 'teacher'}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  Teacher
                </label>
              </div>
            </div>

            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Full Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Full name"
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            {/* Nickname */}
            <div>
              <label htmlFor="nickname" className="block text-sm font-medium text-gray-700">
                Nickname
              </label>
              <input
                id="nickname"
                name="nickname"
                type="text"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Nickname"
                value={formData.nickname}
                onChange={handleChange}
              />
            </div>

            {/* Instrument */}
            <div>
              <label htmlFor="instrument" className="block text-sm font-medium text-gray-700">
                Instrument
              </label>
              <select
                id="instrument"
                name="instrument"
                required
                value={formData.instrument}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="">Select an instrument</option>
                <option value="Bassoon">Bassoon</option>
                <option value="Cello">Cello</option>
                <option value="Clarinet">Clarinet</option>
                <option value="Double Bass">Double Bass</option>
                <option value="Euphonium">Euphonium</option>
                <option value="Flute">Flute</option>
                <option value="Guitar">Guitar</option>
                <option value="Horn">Horn</option>
                <option value="Oboe">Oboe</option>
                <option value="Percussion">Percussion</option>
                <option value="Piano">Piano</option>
                <option value="Saxophone">Saxophone</option>
                <option value="Trombone">Trombone</option>
                <option value="Trumpet">Trumpet</option>
                <option value="Tuba">Tuba</option>
                <option value="Viola">Viola</option>
                <option value="Violin">Violin</option>
                <option value="Voice">Voice</option>
              </select>
            </div>

            {/* Birth Date - Only for students */}
            {formData.role === 'student' && (
              <div>
                <div className="flex items-center space-x-2">
                  <label htmlFor="birthdate" className="block text-sm font-medium text-gray-700">
                    Birth Date (DD-MM-YYYY)
                  </label>
                  <div className="group relative">
                    <svg className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                      <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 w-64 shadow-lg">
                        <div className="text-center">
                          <p className="font-semibold mb-1">Why do we need your birth date?</p>
                          <ul className="text-left space-y-1">
                            <li>• Provides lesson notes specific to your age</li>
                            <li>• Enables age-appropriate badge awards</li>
                            <li>• Includes you in leaderboard competitions</li>
                          </ul>
                        </div>
                        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                </div>
                <input
                  id="birthdate"
                  name="birthdate"
                  type="text"
                  required
                  placeholder="15-03-2010"
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  value={formData.birthdate}
                  onChange={handleChange}
                />
                {formData.birthdate && validateDateFormat(formData.birthdate) && (
                  <p className="mt-1 text-sm text-gray-500">
                    Age: {calculateAge(formData.birthdate)} years old
                  </p>
                )}
                {formData.birthdate && !validateDateFormat(formData.birthdate) && (
                  <p className="mt-1 text-sm text-red-500">
                    Please enter date in DD-MM-YYYY format
                  </p>
                )}
              </div>
            )}

            {/* Practice Frequency - Only for Students */}
            {formData.role === 'student' && (
            <div>
              <div className="flex items-center space-x-2">
                <label htmlFor="practiceSessionsPerWeek" className="block text-sm font-medium text-gray-700">
                  Practice Frequency
                </label>
                <div className="group relative">
                  <svg className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                    <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 w-48 shadow-lg">
                      <div className="text-center">
                        <p>How many times per week will you practice.</p>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </div>
              </div>
              <select
                id="practiceSessionsPerWeek"
                name="practiceSessionsPerWeek"
                value={formData.practiceSessionsPerWeek}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value={1}>1 time per week</option>
                <option value={2}>2 times per week</option>
                <option value={3}>3 times per week</option>
                <option value={4}>4 times per week</option>
                <option value={5}>5 times per week</option>
                <option value={6}>6 times per week</option>
                <option value={7}>7 times per week</option>
              </select>
            </div>
            )}

            {/* Daily Practice Goal - Only for Students */}
            {formData.role === 'student' && (
            <div>
              <div className="flex items-center space-x-2">
                <label htmlFor="practiceSessionDuration" className="block text-sm font-medium text-gray-700">
                  Daily Practice Goal
                </label>
                <div className="group relative">
                  <svg className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                    <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 w-48 shadow-lg">
                      <div className="text-center">
                        <p>Set your daily practice goal.</p>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-1 flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => handleNumberChange('practiceSessionDuration', Math.max(10, formData.practiceSessionDuration - 5))}
                  className="px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  -5
                </button>
                <input
                  id="practiceSessionDuration"
                  name="practiceSessionDuration"
                  type="number"
                  min="10"
                  max="120"
                  value={formData.practiceSessionDuration}
                  onChange={handleChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-center"
                />
                <button
                  type="button"
                  onClick={() => handleNumberChange('practiceSessionDuration', Math.min(120, formData.practiceSessionDuration + 5))}
                  className="px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  +5
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                {formData.practiceSessionDuration} minutes per session (10-120 minutes)
              </p>
            </div>
            )}
          </div>

          {error && (
            <div className="text-red-600 text-sm text-center">{error}</div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading || !isFormValid()}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Saving profile...' : 'Complete Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileSetup;

